import Grid from "@mui/material/Grid/Grid";
import * as React from "react";
import {RefObject, useContext, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {usePeakDetailRepository} from "../../../../api/repositories/PeakDetailRepository";
import {FormPeakAssociationModel} from "../../../../api/types/peak";
import {PeakDetail, PostPeakMatrix} from "../../../../api/types/peakDetail";
import CustomBanner from "../../../../common/components/CustomBanner";
import {FormAutocomplete} from "../../../../common/form-fields";
import {peakCreationAtom} from "../../../../state/peaksRecoil";
import {BASE_DEFINITION_FORM_STEPS, SET_SNACKBAR} from "../../../../constants";
import {SelectElementModelFE} from "../../../../api/types/generic";
import {locationAtom} from "../../../../state/locationsRecoil";
import {useLocationRepository} from "../../../../api/repositories/LocationRepository";
import {Location, LocationModelFE} from "../../../../api/types/location";
import {fromGenericBeToGenericFe, fromSingleGenericBeToSingleGenericFe} from "../../../../utils";
import {useQuery} from "@tanstack/react-query";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";

const PeakAssociation = ({submitRef, refresh}: { submitRef: RefObject<HTMLButtonElement>, refresh: Function }) => {
    const {t} = useTranslation(["translation", "pricing"]);

    const [initialValues] = useState<FormPeakAssociationModel>({
        region: null,
        locations: []
    });

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Form Context
    const {register, setValue, handleSubmit, watch} = useFormContext<FormPeakAssociationModel>();

    // Use Recoil
    const [peakCreation, setPeakCreation] = useRecoilState(peakCreationAtom);
    const {readOnly, inUse, completed, matrixData} = peakCreation;
    const [locations, setLocations] = useRecoilState(locationAtom);

    // Use Repository Hooks
    const {getLocationAll} = useLocationRepository();
    const {postPeakMatrix} = usePeakDetailRepository();
    const {getPeakMatrix} = usePeakDetailRepository();

    // Use State
    const [regions, setRegions] = useState<SelectElementModelFE[]>([]);

    // Use Query
    const locationsQuery = useQuery({
        queryKey: ["pricing_location_query"],
        queryFn: getLocationAll,
    });

    const getStaticData = async () => {
        try {
            if (locationsQuery?.data) {
                const locationsFe: LocationModelFE[] = fromGenericBeToGenericFe<Location, LocationModelFE>(locationsQuery?.data as Location[]).sort((a: LocationModelFE, b: LocationModelFE) => a.label.localeCompare(b.label));
                setLocations([{
                    code: 0,
                    label: t("pricing:actions.selectAll"),
                    region: null
                }, ...locationsFe]);

                setRegions(locationsFe.reduce((acc: LocationModelFE[], el: LocationModelFE) => {
                    const alreadyPresent = acc.find(l => l.region === el.region);
                    if (!alreadyPresent && el.region) acc.push(el);
                    return acc;
                }, []).map(l => ({
                    code: l.region as string,
                    label: l.region as string
                })).sort((a, b) => a.label.localeCompare(b.label)));
            }
        } catch (e) {
        }
    };

    // Use Effect
    useEffect(() => {
        getStaticData();
        // eslint-disable-next-line
    }, [locationsQuery?.data]);

    useEffect(() => {
        if (matrixData) {
            setValue("region", null);
            setValue("locations", matrixData?.map(peakDetail => fromSingleGenericBeToSingleGenericFe<Location, LocationModelFE>(peakDetail.location)));
        }
    }, [matrixData]);

    const watchAllForm = watch();

    const createAssociationRequest = ({
                                          region,
                                          locations
                                      }: FormPeakAssociationModel): PostPeakMatrix => {
        return {
            peakRateId: peakCreation?.peakId || 0,
            locationCodes: locations.map(l => l.code) as string[]
        };
    };

    const i18nDays: { [key: string]: number } = {
        "MONDAY": 1, "TUESDAY": 2, "WEDNESDAY": 3, "THURSDAY": 4, "FRIDAY": 5, "SATURDAY": 6, "SUNDAY": 0,
    }
    const confirmAssociation = async (values: FormPeakAssociationModel) => {
        const request: PostPeakMatrix = createAssociationRequest(values);
        try {
            const newId = await postPeakMatrix(request);
            const matrixData: PeakDetail[] = await getPeakMatrix(newId?.id as number);
            matrixData?.forEach((peakDetail: PeakDetail) => {
                peakDetail.peakDay = peakDetail.peakDay !== null && peakDetail.peakDay !== 'undefined' ? dayjs().day(i18nDays[peakDetail.peakDay as string] || 0).format("dddd").toUpperCase() : t("pricing:days.none");
                peakDetail.peakOffDay = peakDetail.peakOffDay !== null && peakDetail.peakOffDay !== 'undefined' ? dayjs().day(i18nDays[peakDetail.peakOffDay as string] || 0).format("dddd").toUpperCase() : t("pricing:days.none");
            });
            setPeakCreation({
                ...peakCreation,
                matrixData: matrixData,
                step: BASE_DEFINITION_FORM_STEPS.MATRIX,
                completed: [
                    completed[BASE_DEFINITION_FORM_STEPS.DEFINITION],
                    true,
                    completed[BASE_DEFINITION_FORM_STEPS.MATRIX]
                ],
                newId: newId?.id
            });
            refresh()
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const handleChangeRegion = async (value: SelectElementModelFE) => {
        setValue("region", value);

        if (value)
            setValue("locations", locations.filter(location => location.region === value.code));
        else
            setValue("locations", []);
    };

    const handleChangeLocations = (input: LocationModelFE[]) => {
        const value: LocationModelFE[] =
            // è stato cliccato "selectAll
            input.find((el: LocationModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.locations?.find((el: LocationModelFE) => el.code === 0)
                ? // applica il "selectAll
                locations.filter((el: LocationModelFE) => el.code !== 0)
                : // setta solo i car group selezionati
                input.filter((el) => el.code !== 0);
        setValue("locations", value);
    };

    return (
        <form onSubmit={handleSubmit(confirmAssociation)}>
            <Grid container spacing={2} alignItems="start">
                {inUse && <CustomBanner message={t("pricing:peaks.banner.association")} severity={"warning"}/>}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("region")}
                        label={t("pricing:locations.table.headers.region")}
                        options={regions}
                        multiple={false}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        handleChange={handleChangeRegion}
                        readOnly={readOnly}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("locations")}
                        label={`${t("pricing:locations.form.labels.location")}*`}
                        options={locations}
                        multiple={true}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        handleChange={handleChangeLocations}
                        readOnly={readOnly}
                    />
                </Grid>
                <button ref={submitRef} type="submit" style={{display: "none"}}/>
            </Grid>
        </form>
    );
};

export default PeakAssociation;
